'use strict';

const util = require('./../../../app_storefront_core_UI/cartridge/js/util');
const debounce = require('lodash').debounce;
const cache = {};

/**
 * Initializes cache for the navigation component by selecting DOM elements
 */
function initializeCache() {
    cache.headerMainNavigation = document.querySelector('.b-main_navigation');
    cache.headerNavigation = document.querySelector('.b-main_navigation .b-navigation_wrapper');
    cache.headerNavigationItems = document.querySelectorAll('.js-navigation-item, .menu-item-toggle');
    cache.headerNavigationDropdowns = document.querySelectorAll('.js-main_navigation-dropdown');
    cache.navigationTopBackLink = document.querySelector('.navigation-back_link');
    cache.navMenuButton = document.querySelector('.b-header_tools button.menu-toggle');
    cache.closeNavMenuButton = document.querySelector('.b-navigation_wrapper .menu-toggle');
    cache.selectedLocale = document.querySelector('.js-selected-locale');
    cache.currentCountry = document.querySelector('.js-current-country');
}

/**
 * Closes all active navigation dropdowns and removes active states from items
 */
function closeAllActiveNavItems() {
    if (cache.headerNavigation) {
        cache.headerNavigation.querySelectorAll('.open-level').forEach(function(el) {
            el.classList.remove('open-level');
        });
    }

    if (cache.headerNavigationItems) {
        cache.headerNavigationItems.forEach(function(item) {
            item.classList.remove('active');
        });
    }

    if (cache.headerNavigationDropdowns) {
        cache.headerNavigationDropdowns.forEach(function(dropdown) {
            dropdown.style.display = 'none';
        });
    }

    if (cache.navigationTopBackLink) {
        cache.navigationTopBackLink.innerHTML = '';
    }
}

/**
 * Creates back link element for a specific navigation level
 * Returns "ALL" link for first level, or clones parent nav link for deeper levels
 *
 * @param {NodeList} openLevels - Currently open navigation levels
 * @param {number} currentLevelDepth - Current navigation depth (1-based)
 * @returns {Element|null} Link element or null if not found
 */
function createBackLinkForLevel(openLevels, currentLevelDepth) {
    if (currentLevelDepth === 1) {
        const link = document.createElement('a');
        link.classList.add('b-navigation_back-all');
        link.textContent = Resources.ALL;
        return link;
    }

    const parentOpenLevel = openLevels[currentLevelDepth - 2];

    if (!parentOpenLevel) return null;

    const parentActiveItem = parentOpenLevel.querySelector('.js-navigation-item');

    if (!parentActiveItem) return null;

    const parentNavLink = parentActiveItem.querySelector('a[class*="b-navigation"]');

    return parentNavLink ? parentNavLink.cloneNode(true) : null;
}

/**
 * Updates the top back link based on the deepest open navigation level
 * Appends a new back step block without clearing existing ones
 */
function updateTopBackLink() {
    if (!cache.navigationTopBackLink || !cache.headerNavigation) return;

    const openLevels = cache.headerNavigation.querySelectorAll('li.open-level');

    if (openLevels.length === 0) {
        cache.navigationTopBackLink.innerHTML = '';

        return;
    }

    const currentLevelDepth = openLevels.length;
    const existingLevelBlock = cache.navigationTopBackLink.querySelector('[data-nav-level="' + currentLevelDepth + '"]');

    if (existingLevelBlock) return;

    const deepestOpenLevel = openLevels[openLevels.length - 1];
    const activeNavItem = deepestOpenLevel.querySelector('.js-navigation-item');

    if (!activeNavItem) return;

    const menuToggleItem = activeNavItem.querySelector('.menu-item-toggle');

    if (!menuToggleItem) return;

    const levelWrapper = document.createElement('div');

    levelWrapper.classList.add('navigation-back-step');
    levelWrapper.setAttribute('data-nav-level', currentLevelDepth);

    const toggleClone = menuToggleItem.cloneNode(true);

    toggleClone.setAttribute('data-toggle-level', currentLevelDepth);
    toggleClone.classList.add('js-back-step-toggle');

    levelWrapper.appendChild(toggleClone);

    const linkClone = createBackLinkForLevel(openLevels, currentLevelDepth);

    if (linkClone) {
        levelWrapper.appendChild(linkClone);
    }

    cache.navigationTopBackLink.appendChild(levelWrapper);
}

/**
 * Clears inline display styles from navigation dropdowns and main navigation
 * Used to reset styles after mobile menu close or viewport resize to desktop
 */
function clearMenuStyles() {
  cache.headerNavigationDropdowns.forEach(function(dropdown) {
    if (dropdown.style.display === 'none') {
      dropdown.style.display = '';
    }
  });

  if (cache.headerMainNavigation && cache.headerMainNavigation.style.display === 'none') {
    cache.headerMainNavigation.style.display = '';
  }
}

/**
 * Initializes delegated click handler for back step toggles
 * Single listener on parent instead of per-clone listeners to avoid memory leaks
 */
function initBackLinkToggle() {
    if (!cache.navigationTopBackLink || !cache.headerNavigation) return;

    cache.navigationTopBackLink.addEventListener('click', function(e) {
        const toggleClone = e.target.closest('.js-back-step-toggle');

        if (!toggleClone) return;

        e.preventDefault();

        const currentLevelDepth = parseInt(toggleClone.getAttribute('data-toggle-level'), 10);

        cache.navigationTopBackLink.querySelectorAll('.navigation-back-step').forEach(function(step) {
            if (parseInt(step.getAttribute('data-nav-level'), 10) >= currentLevelDepth) {
                step.remove();
            }
        });

        const allActiveToggles = cache.headerNavigation.querySelectorAll('li.open-level .menu-item-toggle.active');
        const originalToggle = allActiveToggles[allActiveToggles.length - 1];

        if (originalToggle) {
            originalToggle.click();
        }
    });
}

/**
 * Initializes click handlers for multi-level menu toggles
 * Handles open/close state and updates back link on each toggle
 */
function initLevelMenuToggle() {
    document.querySelectorAll('.js-toggled_menu .menu-item-toggle').forEach(function(toggle) {
        toggle.addEventListener('click', function(e) {
            e.preventDefault();

            const parentLi = e.currentTarget.closest('li, .js-navigation-item');

            if (!parentLi) return;

            parentLi.classList.toggle('active');

            const parentUl = parentLi.parentElement;

            if (parentUl) {
                parentUl.classList.toggle('open-level');

                if (parentUl.parentElement) {
                    parentUl.parentElement.classList.toggle('open-level');
                }
            }

            updateTopBackLink();
        });
    });
}

/**
 * Initializes keyboard event listeners
 * Closes navigation on Escape key press (desktop only)
 */
function initKeyboardEvents() {
    if (util.isMobile()) return;

    document.addEventListener('keydown', function(e) {
        if (e.key !== 'Escape') return;

        const isNavActive = cache.headerMainNavigation && cache.headerMainNavigation.classList.contains('active');
        const isLocaleActive = cache.selectedLocale && cache.selectedLocale.classList.contains('active');
        const isCountryActive = cache.currentCountry && cache.currentCountry.classList.contains('active');

        if (isNavActive || isLocaleActive || isCountryActive) {
            closeAllActiveNavItems();
        }
    });
}

function closeNavMenu() {
  if (cache.navMenuButton && cache.navMenuButton.classList.contains('active')) {
    cache.navMenuButton.click();

    closeAllActiveNavItems();
  }
}

/**
 * Initializes all navigation event listeners
 * Binds click, touch, keyboard and resize events
 */
function initializeEvents() {
    initKeyboardEvents();
    initBackLinkToggle();

    if (cache.headerNavigation) {
      cache.headerNavigation.addEventListener('pointerdown', function(e) {
        e.stopPropagation();
      });
    }

    if (cache.closeNavMenuButton) {
      cache.closeNavMenuButton.addEventListener('click', closeNavMenu);
    }

    if (cache.headerMainNavigation && cache.closeNavMenuButton) {
        cache.headerMainNavigation.addEventListener('pointerdown', function() {
            closeNavMenu();
        });
    }

    window.addEventListener('resize', debounce(function() {
        if (cache.headerMainNavigation && cache.headerMainNavigation.classList.contains('active') && cache.closeNavMenuButton) {
            closeNavMenu();
        }

        if (window.innerWidth > 1023) {
          clearMenuStyles();
        }
    }, 150));

    initLevelMenuToggle();
}

initializeCache();
initializeEvents();